// Mobile-safe form-in-modal layout, applied globally.
//
// Chakra's `ModalBody` has `flex: 1 + overflow: auto` when `scrollBehavior="inside"`, which only
// produces an internal scroll region if (a) the dialog itself is height-bounded and (b) every
// ancestor between the dialog and the body is a flex column with `min-height: 0`. When a form
// wraps `ModalBody` and `ModalFooter` (a common pattern), the form's default `display: block`
// breaks the flex chain: the body never bounds, content paints past the dialog, and on full-
// height mobile modals the underlying page bleeds through. Re-typing the form as a flex pass-
// through fixes every such modal at the theme level instead of per-component.
export const globalStyles = {
  '.chakra-modal__content > form': {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 0%',
    minHeight: 0,
    minWidth: 0,
    width: '100%',
  },
  '*:focus-visible': {
    boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.35)',
    outline: 'none',
    _dark: {
      boxShadow: '0 0 0 3px rgba(50, 132, 254, 0.35)',
    },
  },
  body: {
    fontVariantNumeric: 'tabular-nums',
    // Explicit page background. Light keeps Chakra's effective default (white); dark uses the
    // navy app shell so cards sit one elevation step above the page.
    bg: 'app.bg',
    _dark: {
      bg: 'app.bg',
    },
  },
  // Chakra Skeleton/SkeletonText default to gray.100 → gray.400 in component code, ignoring
  // theme baseStyle for the gradient. Force semantic shimmer tokens app-wide.
  '.chakra-skeleton': {
    backgroundImage:
      'linear-gradient(270deg, var(--chakra-colors-skeleton-start), var(--chakra-colors-skeleton-end), var(--chakra-colors-skeleton-end), var(--chakra-colors-skeleton-start)) !important',
  },
  // Defensive, app-wide fallback for `prefers-reduced-motion: reduce`. This only catches plain
  // CSS `transition`/`animation` props already scattered through the codebase (e.g. the
  // inline `transition="background 0.15s"`-style props in QueueList.tsx, TheatreStockSection.tsx).
  // It does NOT reach Chakra's own Collapse/Modal/Drawer/Menu/Toast animations — those animate
  // via framer-motion/the Web Animations API, not plain CSS transitions, so this media query
  // can't gate them. Deeper reduced-motion compliance for Chakra's internal framer-motion
  // animations would require wrapping/patching Chakra itself — a known, accepted limitation,
  // not attempted here.
  '@media (prefers-reduced-motion: reduce)': {
    '*, *::before, *::after': {
      animationDuration: '0.01ms !important',
      animationIterationCount: '1 !important',
      transitionDuration: '0.01ms !important',
      scrollBehavior: 'auto !important',
    },
  },
}
